import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  OnDestroy,
  ViewChild,
  computed,
  inject,
  input,
} from '@angular/core';
import type * as Highcharts from 'highcharts';
import { HighchartsChartComponent } from 'highcharts-angular';
import { ChartThemeService } from 'src/app/ui-kit/services/chart-theme.service';
import { buildBrandChartOptions } from 'src/app/ui-kit/chart/base-chart-options';

/** A named series for the bar chart. */
export interface BarChartSeries {
  name: string;
  data: number[];
  color?: string;
  /**
   * Optional per-bar colours, index-aligned with `data`. When set, each bar
   * uses its own colour (overriding `color`); entries may be `undefined` to
   * fall back to the series/theme colour. Use for severity-coded categories.
   */
  pointColors?: (string | undefined)[];
}

/**
 * Brand-themed Highcharts column (vertical bar) chart. Use for categorical comparisons.
 * For trend data, use `LineChartComponent`.
 *
 * A ResizeObserver on the wrapper calls the chart's `reflow()` whenever the
 * container's width changes — the side-nav toggle on admin pages (and any
 * grid-column span change) shifts the cell width after initial render, and
 * Highcharts only reflows on window resize by default, which caused charts
 * to overflow their card.
 *
 * @example
 * <app-bar-chart
 *   [categories]="['Mon', 'Tue', 'Wed', 'Thu', 'Fri']"
 *   [series]="[{ name: 'Observations', data: [3, 5, 2, 7, 4] }]"
 *   ariaLabel="Observations per weekday"
 * />
 *
 * @feature shared
 */
@Component({
  selector: 'app-bar-chart',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [HighchartsChartComponent],
  host: { class: 'block w-full' },
  template: `
    <div #chartHost class="w-full" [style.height.px]="height()" [attr.role]="'img'" [attr.aria-label]="ariaLabel()">
      <highcharts-chart
        [options]="options()"
        (chartInstance)="onChartCreated($event)"
        style="width: 100%; height: 100%; display: block;"
      />
    </div>
  `,
})
export class BarChartComponent implements AfterViewInit, OnDestroy {
  private readonly themeService = inject(ChartThemeService);

  readonly categories = input.required<string[]>();
  readonly series = input.required<BarChartSeries[]>();
  readonly height = input<number>(320);
  readonly xAxisTitle = input<string | undefined>(undefined);
  readonly yAxisTitle = input<string | undefined>(undefined);
  readonly ariaLabel = input.required<string>();
  /**
   * Stack series in each category instead of grouping them side-by-side.
   * `'normal'` stacks raw values; `'percent'` normalises every category to
   * 100% — useful when category sample sizes differ. Leave `undefined` to
   * keep the default grouped-column behaviour.
   */
  readonly stacking = input<'normal' | 'percent' | undefined>(undefined);

  @ViewChild('chartHost', { static: true }) chartHost!: ElementRef<HTMLDivElement>;

  private chartInstance: Highcharts.Chart | null = null;
  private resizeObserver: ResizeObserver | null = null;

  readonly options = computed<Highcharts.Options>(() => {
    const base = buildBrandChartOptions(this.themeService.theme()) as Highcharts.Options;
    const stack = this.stacking();
    return {
      ...base,
      chart: { ...base.chart, type: 'column', height: this.height() },
      xAxis: {
        ...(base.xAxis as Highcharts.XAxisOptions),
        type: 'category',
        categories: this.categories(),
        title: { text: this.xAxisTitle() },
      },
      yAxis: {
        ...(base.yAxis as Highcharts.YAxisOptions),
        title: { text: this.yAxisTitle() },
      },
      plotOptions: {
        ...(base.plotOptions ?? {}),
        column: {
          ...(base.plotOptions?.column ?? {}),
          ...(stack ? { stacking: stack } : {}),
        },
      },
      series: this.series().map(s => ({
        type: 'column',
        name: s.name,
        color: s.color,
        data: s.pointColors ? s.data.map((y, i) => ({ y, color: s.pointColors?.[i] })) : s.data,
      })) as Highcharts.SeriesOptionsType[],
    };
  });

  onChartCreated(chart: Highcharts.Chart): void {
    this.chartInstance = chart;
    // Reflow immediately once the instance is available — this covers the
    // case where initial measurement ran before the grid cell had settled.
    queueMicrotask(() => chart.reflow());
  }

  ngAfterViewInit(): void {
    if (typeof ResizeObserver === 'undefined' || !this.chartHost?.nativeElement) return;
    this.resizeObserver = new ResizeObserver(() => this.chartInstance?.reflow());
    this.resizeObserver.observe(this.chartHost.nativeElement);
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
    this.resizeObserver = null;
    this.chartInstance = null;
  }
}
